import { z } from 'zod'

/* Device-API */

export interface LoraStatus {
  backwardFlow: boolean
  batteryWarning: boolean
  emptyTubeDetected: boolean
  fraudDetected: boolean
  freezeDetected: boolean
  hardwareError: boolean
  leakage: boolean
  magneticFraud: boolean
  moduleRemoved: boolean
  opticalFraud: boolean
  resetDetected: boolean
  switchError: boolean
  tooBigFlow: boolean
}

export enum DeviceType {
  LORA = 'LORA',
  ADEUNIS = 'ADEUNIS',
  ADEUNIS_v3 = 'ADEUNIS_v3',
  BMETERS_v2 = 'BMETERS_v2',
  BMETERS_COMBO = 'BMETERS_COMBO',
  BMETERS_LORA_PULSE = 'BMETERS_LORA_PULSE',
  HYDROSONIC_M1 = 'HYDROSONIC_M1',
  KOKA_W1 = 'KOKA_W1'
}

export interface DeviceAlarmSettings {
  tooBigFlowEnabled: boolean
  backwardFlowEnabled: boolean
  magneticFraudEnabled: boolean
  opticalFraudEnabled: boolean
  moduleRemovedEnabled: boolean
  batteryWarningEnabled: boolean
  leakageEnabled: boolean
  hardwareErrorEnabled: boolean
  switchErrorEnabled: boolean
  resetDetectedEnabled: boolean
  fraudDetectedEnabled: boolean
  freezeDetectedEnabled: boolean
  emptyTubeDetectedEnabled: boolean
  flowLimitEnabled: boolean
  maxFlowPerHour?: number
}

/**
 * Type for 'DeviceMeterDataHistoryResponseDto' (backend API data type).
 */
export interface DeviceMeterDataHistoryResponseItem {
  id: string
  time: number
  temperature?: number
  waterUsed?: number
}

/**
 * Schema for 'DeviceMeterDataHistoryResponseDto' (backend API data type).
 */
export const DeviceMeterDataHistoryResponseItemSchema = z.object({
  id: z.string(),
  time: z.number(),
  temperature: z.number().nullish().transform(it => it ?? undefined),
  waterUsed: z.number().nullish().transform(it => it ?? undefined)
})

/**
 * Schema for 'DeviceMeterDataHistoryResponseDto' list response.
 */
export const DeviceMeterDataHistoryResponseItemsSchema = z.array(DeviceMeterDataHistoryResponseItemSchema)

/**
 * Type for 'DeviceResponseItemDto' (backend API data type).
 */
export interface DeviceResponseItem {
  id: string
  type: DeviceType
  devEui: string
  alarmSettings?: DeviceAlarmSettings
  baseValue?: number
  companyId: string
  deleted: boolean
  firstMeterDataEntry?: number
  flowLimitAlertActive: boolean
  labels: string[]
  latestLoraStatus?: LoraStatus
  leakageWarningActive: boolean
  location?: string
  pulseInLiters?: number
  sendTemperature: boolean
  serialNumber?: string
  temperatureAlertActive: boolean
  transmitterSerialNumber?: string
  updatedAt?: number
  waterUsed?: number
}

/**
 * Schema for 'DeviceResponseItemDto' (backend API data type).
 */
export const DeviceResponseItemSchema = z.object({
  id: z.string(),
  type: z.nativeEnum(DeviceType),
  devEui: z.string(),
  alarmSettings: z.object({
    tooBigFlowEnabled: z.boolean(),
    backwardFlowEnabled: z.boolean(),
    magneticFraudEnabled: z.boolean(),
    opticalFraudEnabled: z.boolean(),
    moduleRemovedEnabled: z.boolean(),
    batteryWarningEnabled: z.boolean(),
    leakageEnabled: z.boolean(),
    hardwareErrorEnabled: z.boolean(),
    switchErrorEnabled: z.boolean(),
    resetDetectedEnabled: z.boolean(),
    fraudDetectedEnabled: z.boolean(),
    freezeDetectedEnabled: z.boolean(),
    emptyTubeDetectedEnabled: z.boolean(),
    flowLimitEnabled: z.boolean(),
    maxFlowPerHour: z.number().nullish().transform(it => it ?? undefined)
  }).nullish().transform(it => it ?? undefined),
  baseValue: z.number().nullish().transform(it => it ?? undefined),
  companyId: z.string(),
  deleted: z.boolean(),
  firstMeterDataEntry: z.number().nullish().transform(it => it ?? undefined),
  flowLimitAlertActive: z.boolean(),
  labels: z.array(z.string()),
  latestLoraStatus: z.object({
    backwardFlow: z.boolean(),
    batteryWarning: z.boolean(),
    emptyTubeDetected: z.boolean(),
    fraudDetected: z.boolean(),
    freezeDetected: z.boolean(),
    hardwareError: z.boolean(),
    leakage: z.boolean(),
    magneticFraud: z.boolean(),
    moduleRemoved: z.boolean(),
    opticalFraud: z.boolean(),
    resetDetected: z.boolean(),
    switchError: z.boolean(),
    tooBigFlow: z.boolean()
  }).nullish().transform(it => it ?? undefined),
  leakageWarningActive: z.boolean(),
  location: z.string().nullish().transform(it => it ?? undefined),
  pulseInLiters: z.number().nullish().transform(it => it ?? undefined),
  sendTemperature: z.boolean(),
  serialNumber: z.string().nullish().transform(it => it ?? undefined),
  temperatureAlertActive: z.boolean(),
  transmitterSerialNumber: z.string().nullish().transform(it => it ?? undefined),
  updatedAt: z.number().nullish().transform(it => it ?? undefined),
  waterUsed: z.number().nullish().transform(it => it ?? undefined)
})

/**
 * Schema for 'DeviceResponseItemDto' list response.
 */
export const DeviceResponseItemsSchema = z.array(DeviceResponseItemSchema)
